import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { offlineDataManager } from '@shared/lib/offlineData';
import { offlineQueueManager } from '@shared/lib/offlineQueue';
import { storage } from '@shared/lib/storage';

interface UserInfo {
  name: string;
  email: string;
  avatar: string;
  user_id: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseUserFromToken(token: string): UserInfo | null {
  try {
    const decoded: any = jwtDecode(token);
    return {
      name: decoded.name,
      email: decoded.email || '',
      avatar: decoded.avatar || decoded.picture || '',
      user_id: decoded.user_id || decoded.sub || '',
    };
  } catch {
    return null;
  }
}

const readToken = (): string | null => {
  try {
    return storage.getItem('hintword_token');
  } catch {
    return null;
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize synchronously from storage so a logged-in user renders the app
  // immediately (no flash of the public landing page on first paint).
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!readToken());
  const [user, setUser] = useState<UserInfo | null>(() => {
    const token = readToken();
    return token ? parseUserFromToken(token) : null;
  });

  useEffect(() => {
    const token = storage.getItem('hintword_token');
    if (token) {
      setIsAuthenticated(true);
      setUser(parseUserFromToken(token));
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  const login = (token: string) => {
    storage.setItem('hintword_token', token);
    setIsAuthenticated(true);
    setUser(parseUserFromToken(token));
  };

  const logout = () => {
    storage.removeItem('hintword_token');
    // Offline-first: clear local data + pending queue so the next login
    // re-seeds from the server rather than showing the previous user's data.
    offlineQueueManager.clearQueue();
    offlineDataManager.clearAll();
    try {
      storage.removeItem('hintword_last_sync_time');
    } catch {}
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 